import React, { useMemo } from 'react';
import './index.css';
import { useState } from 'react';
import { ReactComponent as ArrowRight } from './icons/arrow-right.svg';
import { ReactComponent as ArrowLeft } from './icons/arrow-left.svg';
import { useTheme } from '@mui/material';
import styled from '@emotion/styled';
import StatusLabel from 'common/components/StatusLabel';

export const monthLabels: string[] = [
  'Січень',
  'Лютий',
  'Березень',
  'Квітень',
  'Травень',
  'Червень',
  'Липень',
  'Серпень',
  'Вересень',
  'Жовтень',
  'Листопад',
  'Грудень',
];
export const dayLabels: string[] = [
  'Пн',
  'Вт',
  'Ср',
  'Чт',
  'Пт',
  'Сб',
  'Нд',
];

export function useCalendar() {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  const handleDateChange = (date: Date) => {
    setSelectedDate(date);
  };

  return {
    selectedDate,
    handleDateChange,
  };
}

type ChangeMonthProps = [number, boolean];
type DateProps = {
  day: number,
  month: number,
  year: number,
};

const CalendarNavigationSide = styled.div`
  display:flex;
  align-items: center;
  gap:4px;
  cursor: pointer;
  transition: all 0.2s;

  & svg, & path, & span {
    stroke: ${({ theme }) => theme.colors.black}; 
  }
  &:hover svg, &:hover span, &:hover path {
    color: ${({ theme }) => theme.colors.primaryColor};
    stroke: ${({ theme }) => theme.colors.primaryColor};
  }
`;

const CalendarWrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.gray};
    border-radius: 16px;
    padding:16px;
    flex: 1;
    height: calc(100vh - 262px);
    display: flex;
    flex-direction: column;
`;

const CalendarWeekday = styled.div`
  padding: 8px;
  border-right: 1px solid ${({ theme }) => theme.colors.gray};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray};
  display:flex;
  justify-content: center;
  height: 50px;
  flex-basis: calc(100% / 7);
  font-family: Mulish;
font-size: 20px;
font-style: normal;
font-weight: 500;
line-height: 100%; /* 20px */
letter-spacing: 1px;
  &:last-of-type {
    border-right: none;
  }
`;

const CalendarDayStyled = styled.div`
  padding: 8px 16px 16px 8px;
  border-right: 1px solid ${({ theme }) => theme.colors.gray};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray};
  flex-basis: calc(100% / 7);
  display:flex;
  height:auto;
  justify-content: space-between;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  gap: 8px;
  &:nth-of-type(7n) {
    border-right: none;
  }
  &:nth-last-of-type(-n+7) {
    border-bottom: none;
  }
`;

const CalendarDayNumber = styled.div`
  height:34px;
  width:34px;
  display:flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    &.active {
        // background is primary color but with 0.25 opacity
        background-color: ${(props) => props.theme.colors.primaryColor + '40'};
    }
`;

type EventProps = {
  label: string,
  color?: string,
  date: string,
};

const mockEvents: EventProps[] = [
  {
    label: 'Event 1',
    color: '#FF0000',
    date: '01-01-2023',
  },
  {
    label: 'Event 2',
    color: '#00FF00',
    date: '15-02-2023',
  },
  {
    label: 'Event 3',
    color: '#0000FF',
    date: '28-03-2023',
  },
  {
    label: 'Event 4',
    color: '#FFFF00',
    date: '10-04-2023',
  },
  {
    label: 'Event 5',
    color: '#00FFFF',
    date: '22-05-2023',
  },
  {
    label: 'Event 6',
    color: '#FF00FF',
    date: '05-06-2023',
  },
  {
    label: 'Event 7',
    color: '#C0C0C0',
    date: '18-07-2023',
  },
  {
    label: 'Event 8',
    color: '#808080',
    date: '30-08-2023',
  },
  {
    label: 'Event 9',
    color: '#800000',
    date: '12-09-2023',
  },
  {
    label: 'Event 10',
    color: '#008000',
    date: '12-09-2023',
  },
  {
    label: 'Event 10safd saf sd fassdf asdf a fsda dsfa sdf as',
    color: '#008000',
    date: '12-09-2023',
  },
  {
    label: 'Event 120',
    color: '#008000',
    date: '12-09-2023',
  },
  {
    label: 'Event 11',
    color: '#000080',
    date: '06-11-2023',
  },
  {
    label: 'Event 12',
    color: '#800080',
    date: '19-12-2023',
  },
];

export default function CalendarPage() {
  const theme = useTheme();
  const { selectedDate } = useCalendar();
  const currentDate = { day: selectedDate.getDate(), month: selectedDate.getMonth(), year: selectedDate.getFullYear() };
  const [date, setDate] = useState<DateProps>({ day: selectedDate.getDate(), month: selectedDate.getMonth(), year: selectedDate.getFullYear() });
  const [eventList] = useState<EventProps[]>(mockEvents);


  // Get the first day's of the month weekday (0-6)
  let firstDayWeekday = new Date(date.year, date.month, 1).getDay() - 1;
  if (firstDayWeekday === -1) firstDayWeekday = 6;
  // Get the last day's of the month weekday (0-6)
  let lastDayWeekday = new Date(date.year, date.month + 1, 0).getDay() - 1;
  if (lastDayWeekday === -1) lastDayWeekday = 6;

  // Next month [0-11, true/false (should change year)]
  const nextMonth: ChangeMonthProps = useMemo(() => date.month === 11 ? [0, true] : [date.month + 1, false], [date.month]);
  // Previous month [0-11, true/false (should change year)]
  const prevMonth: ChangeMonthProps = useMemo(() => date.month === 0 ? [11, true] : [date.month - 1, false], [date.month]);
  const daysInMonth = new Date(date.year, date.month + 1, 0).getDate();

  const handleNextMonth = () => {
    setDate((prev) => {
      if (prev.month === 11) {
        return {
          day: prev.day,
          month: 0,
          year: prev.year + 1,
        };
      }
      return {
        day: prev.day,
        month: prev.month + 1,
        year: prev.year,
      };
    });
  };
  const handlePrevMonth = () => {
    setDate((prev) => {
      if (prev.month === 0) {
        return {
          day: prev.day,
          month: 11,
          year: prev.year - 1,
        };
      }
      return {
        day: prev.day,
        month: prev.month - 1,
        year: prev.year,
      };
    });
  };

  type CalendarDayProps = {
    day: number,
    disabled?: boolean,
    month: number,
  };

  const CalendarDay = ({ day, disabled, month }: CalendarDayProps) => {
    // Get events array for the day
    let events = [];
    if (!disabled) {
      events = eventList.filter((event) => {
        const eventDate = new Date(event.date);
        return eventDate.getDate() === day && eventDate.getMonth() === month && eventDate.getFullYear() === date.year;
      });
    }
    // Show only 3 events
    return (
            <CalendarDayStyled>
                <CalendarDayNumber className={month === currentDate.month && currentDate.day === day && currentDate.year === date.year ? 'active' : ''} style={{ color: disabled ? theme.colors.gray : theme.colors.primaryTextColor }}>
                    {day}
                </CalendarDayNumber>
                <div className='calendar-day-events'>
                    {events.length > 0 && !disabled ? events.slice(0, 2).map((event, index) => (
                        <StatusLabel key={index} color={event.color} status={event.label} />
                    )) : null}
                </div>
            </CalendarDayStyled>
    );
  };

  return (
        <div className="calendar-section">
            <div className='calendar-navigation-line'>
                <div className='calendar-navigation-side-wrapper'>
                    <CalendarNavigationSide
                        onClick={handlePrevMonth}
                    >
                        <ArrowLeft className='svg-black' />
                        <span className="calendar-navigation-side-title">{monthLabels[prevMonth[0]]}</span>
                        <span style={{ color: theme.colors.gray }} className='calendar-navigation-side-title'>{prevMonth[1] ? date.year - 1 : date.year}</span>
                    </CalendarNavigationSide>
                </div>
                <span className='calendar-navigation-middle'>
                    <span className='calendar-navigation-middle-title'>{monthLabels[date.month]} </span>
                    <span className='calendar-navigation-middle-subtitle'>{date.year}</span>
                </span>
                <div className='calendar-navigation-side-wrapper'>
                    <CalendarNavigationSide
                        onClick={handleNextMonth}
                    >
                        <span className='calendar-navigation-side-title'>{monthLabels[nextMonth[0]]}</span>
                        <span style={{ color: theme.colors.gray }} className='calendar-navigation-side-title'>{nextMonth[1] ? date.year + 1 : date.year}</span>
                        <ArrowRight className='svg-black' />
                    </CalendarNavigationSide>
                </div>
            </div>
            <CalendarWrapper>
                <div className='calendar-weekday-wrapper'>
                    {dayLabels.map((day) => (
                        <CalendarWeekday key={day}>
                            {day}
                        </CalendarWeekday>
                    ))}
                </div>
                <div className='calendar-day-divider'>
                    {/* Map month days from first day of the week, if it isn't a 0, then map days from previous month */}
                    {Array.from(Array(firstDayWeekday === 0 ? 0 : firstDayWeekday).keys()).map((day) => (
                        <CalendarDay key={day} disabled month={prevMonth[0]} day={new Date(date.year, date.month, 0).getDate() - day} />
                    ))}
                    {/* Map month days */}
                    {Array.from(Array(daysInMonth).keys()).map((day) => (
                        <CalendarDay month={date.month} key={day} day={day + 1} />
                    ))}
                    {/* Map month days from last day of the week, if it isn't a 6, then map days from next month using lastDayWeekday */}
                    {Array.from(Array(lastDayWeekday === 6 ? 0 : 6 - lastDayWeekday).keys()).map((day) => {
                      
                      return (
                        <CalendarDay month={nextMonth[0]} key={day} disabled day={day + 1} />
                      );
                    })}
                    {/* If there is only 5 rows, then add 7 more days from the next month */}
                    {daysInMonth + firstDayWeekday + 6 - lastDayWeekday <= 35 && Array.from(Array(7).keys()).map((day) => (
                        <CalendarDay month={nextMonth[0]} key={day} disabled day={day + (6 - lastDayWeekday) + 1} />
                    ))}
                    {daysInMonth + firstDayWeekday + 6 - lastDayWeekday + 7 <= 35 && Array.from(Array(7).keys()).map((day) => (
                        <CalendarDay month={nextMonth[0]} key={day} disabled day={day + (6 - lastDayWeekday) + 7 + 1} />
                    ))}
                </div>
            </CalendarWrapper>
        </div>
  );
}






